import { IBaseModel } from "../BaseModel";


export interface IReportDateField {
  key: string;
  label: string;
}

export interface IReport extends IBaseModel {
  title: string;
  rawSql: string;
  /**
   * Legacy flag: both dates required, filtered on whichever column the SQL hardcoded. Only
   * meaningful while dateFields is empty. Removed once every report has been rewritten.
   */
  allowDates: boolean;
  /**
   * JSON array of { key, label } date columns this report can filter on. The key is bound into
   * the SQL as @DateField; null or empty means the report offers no date field selection.
   */
  dateFields: string | null;
}

/** Reads the stored JSON, tolerating anything unusable as "no date fields". */
export function parseReportDateFields(dateFields: string | null | undefined): IReportDateField[] {
  if (!dateFields || !dateFields.trim()) return [];

  try {
    const parsed = JSON.parse(dateFields);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((x) => x && typeof x.key === "string" && x.key.trim())
      .map((x) => ({ key: x.key, label: x.label || x.key }));
  } catch {
    return [];
  }
}

/** Renders the stored JSON as one `key|Label` line per field, for editing. */
export function dateFieldsToText(dateFields: string | null | undefined): string {
  return parseReportDateFields(dateFields)
    .map((field) => `${field.key}|${field.label}`)
    .join("\n");
}

/**
 * Turns the edited `key|Label` lines back into the stored JSON.
 *
 * Returns null when nothing usable is left, so clearing the box puts the report back to NULL
 * (no date field selection) rather than storing an empty array. A line with no pipe uses the
 * key as its own label.
 */
export function textToDateFieldsJson(text: string | null | undefined): string | null {
  if (!text || !text.trim()) return null;

  const fields = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const separator = line.indexOf("|");
      const key = (separator === -1 ? line : line.slice(0, separator)).trim();
      const label = separator === -1 ? key : line.slice(separator + 1).trim();
      return { key, label: label || key };
    })
    .filter((field) => field.key.length > 0);

  return fields.length > 0 ? JSON.stringify(fields) : null;
}

/** Short summary for the reports list. */
export function summariseDateFields(dateFields: string | null | undefined): string {
  const fields = parseReportDateFields(dateFields);
  return fields.length > 0 ? fields.map((f) => f.label).join(", ") : "-";
}
